import type { ComponentType } from 'react'
import { KIND, StyledLoadingSpinner } from 'baseui/button'
import { useTranslation } from 'react-i18next'
import BaseSDButton from '@/shared/components/SDComponentsNewDesign/SDButton/SDButton'
import IconAlertTriangle from '@/shared/icons/NewDesign/IconAlertTriangle'
import IconListBulleted from '@/shared/icons/NewDesign/IconListBulleted'
import IconRefresh from '@/shared/icons/NewDesign/IconRefresh'
import ContractRow from '../ContractRow/ContractRow'
import StateRow from './StateRow/StateRow'
import type { ContractsBodyState } from '../useContractsCard'
import { Contract } from '../../../types'
import { StateBox, StateIcon, StateTitle, VisuallyHiddenText } from '../styled-components'

const SDButton = BaseSDButton as ComponentType<any>

interface ContractsBodyProps {
  state: ContractsBodyState
  contracts: Contract[]
  getContracts: () => void
  formInitUrl: string
  onRetry: () => void
  canReset: boolean
  onReset: () => void
}

const ContractsBody = ({
  state,
  contracts,
  getContracts,
  formInitUrl,
  onRetry,
  canReset,
  onReset,
}: ContractsBodyProps) => {
  const { t } = useTranslation()

  if (state === 'loading') {
    return (
      <StateRow>
        <StateBox role='status'>
          <StyledLoadingSpinner />
          <VisuallyHiddenText>{t('dashboard:overview.loading')}</VisuallyHiddenText>
        </StateBox>
      </StateRow>
    )
  }

  if (state === 'error') {
    return (
      <StateRow>
        <StateBox role='alert'>
          <StateIcon>
            <IconAlertTriangle size={24} />
          </StateIcon>
          <StateTitle>{t('dashboard:overview.load_error')}</StateTitle>
          <SDButton kind={KIND.secondary} onClick={onRetry}>
            {t('dashboard:overview.retry')}
          </SDButton>
        </StateBox>
      </StateRow>
    )
  }

  if (state === 'empty') {
    return (
      <StateRow>
        <StateBox role='status'>
          <StateIcon>
            <IconListBulleted size={24} />
          </StateIcon>
          <StateTitle>{t('contractsTable:no_contracts_found')}</StateTitle>
          <SDButton
            kind={KIND.secondary}
            hidden={!canReset}
            onClick={onReset}
            startEnhancer={<IconRefresh size={16} />}
          >
            {t('dashboard:overview.reset_filters')}
          </SDButton>
        </StateBox>
      </StateRow>
    )
  }

  return (
    <>
      {contracts.map(contract => (
        <ContractRow key={contract.id} contract={contract} getContracts={getContracts} formInitUrl={formInitUrl} />
      ))}
    </>
  )
}

export default ContractsBody
